import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormApi } from 'final-form';

import {
	CheckinPassengerFragment,
	GdsService_OrderAdditionalServiceGdsServiceVip_Fragment,
	InputTraveller,
	OrderAdditionalServiceGdsServiceServiceType,
	PassengerFragment,
	useAddTravellersDataMutation,
	useUpdateOrderMutation
} from '@websky/graphql';

import { fillOrder } from '../../../Checkout/store/order/actions';
import { useOrderService } from '../hooks';

import { getChangedServicesToSave, getUpdatedTravellersPhones, SmsPopupFormValues } from './utils';
import { setServicesIsSaving, setServicesSaved } from '../../../Checkout/store/servicesIsSaving/actions';
import { isServicesSaving } from '../../../Checkout/store/servicesIsSaving/selectors';

export const useSmsNotifications = (
	services: GdsService_OrderAdditionalServiceGdsServiceVip_Fragment[],
	travellers: (PassengerFragment | CheckinPassengerFragment)[],
	orderId: string,
	onClose: () => void
) => {
	const dispatch = useDispatch();

	const isOrderServicesSaving = useSelector(isServicesSaving);

	const { onSaveHandler, onRemoveHandler, isSelected } = useOrderService(
		services,
		OrderAdditionalServiceGdsServiceServiceType.Vip,
		true
	);

	const [addTravellersDataMutation] = useAddTravellersDataMutation();
	const [updateOrder] = useUpdateOrderMutation();

	const [updateTravellers, setUpdateTravellers] = useState<InputTraveller[]>([]);

	useEffect(() => {
		// a workaround for displaying the loader during order updates
		if (!isOrderServicesSaving && updateTravellers.length) {
			setTimeout(() => onUpdateTravellers(updateTravellers));
			setUpdateTravellers([]);
		}
	}, [isOrderServicesSaving, updateTravellers]);

	const onUpdateTravellers = async (travellersData: InputTraveller[]) => {
		dispatch(setServicesIsSaving());

		try {
			await addTravellersDataMutation({
				variables: {
					variables: {
						orderId,
						travellersData
					}
				}
			});

			const { data } = await updateOrder({ variables: { id: orderId } });

			if (data?.UpdateOrder) {
				dispatch(fillOrder(data.UpdateOrder));
			}
		} catch (e) {
			console.log(e);
		}

		dispatch(setServicesSaved());
	};

	const onSubmit = async (values: SmsPopupFormValues, form: FormApi<SmsPopupFormValues>) => {
		onClose();

		const dirtyFields = form.getState().dirtyFields;
		const { servicesToAdd, servicesToRemove } = getChangedServicesToSave(values, services[0], dirtyFields);

		if (servicesToRemove.length) {
			onRemoveHandler(servicesToRemove);
		}

		if (servicesToAdd.length) {
			onSaveHandler(servicesToAdd);
		}

		setUpdateTravellers(getUpdatedTravellersPhones(values, dirtyFields, travellers));
	};

	return { onSubmit, isSelected, isLoading: isOrderServicesSaving };
};
